import chroma from 'chroma-js';
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { MapContainer, TileLayer, GeoJSON, useMap, Pane } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Tooltip as AntTooltip, Spin, Button, Tag, message, Input, List, Switch } from 'antd';
import { MdLightMode, MdDarkMode, MdSatelliteAlt } from 'react-icons/md';
import {
  PlayCircleOutlined,
  DeleteOutlined,
  ArrowLeftOutlined,
  SelectOutlined,
  CloseOutlined,
  SwapRightOutlined,
  StopOutlined,
  SearchOutlined,
  ArrowRightOutlined,
  FullscreenOutlined
} from '@ant-design/icons';
import { ExchangeIcon } from '../../constants/icons';
import ModuleSlider from '../../components/map/ModuleSlider';
import ZoomButtons from '../../components/map/ZoomButtons';
import CurrentLocationButton from '../../components/map/CurrentLocationButton';
import { DataLoadingScreen } from '../../components/common';
import { useLoading } from '../../contexts/LoadingContext';
import { getClientType } from '../../services/clientConfig';
import { fetchClientJSON, getAuthHeaders } from '../../services/clientDataService';
import { SEMANTIC_COLORS } from '../../constants/colors';
import * as turf from '@turf/turf';
import '../../styles/mappa.css'; // Import global map styles for sidebar-button classes
import './AssignxPage.css';

const BASE_FLOWS_FILE = 'assignx/assignx_network.geojson';
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5050';

const DEFAULT_CENTER = [44.309405, 9.352045];
const DEFAULT_ZOOM = 13;
const VIEWPORT_BUFFER = 0.15;

/* ── Helpers ── */
function simplifyCoords(coords) {
  if (typeof coords[0] === 'number') {
    return coords.map(c => Math.round(c * 1e6) / 1e6);
  }
  return coords.map(simplifyCoords);
}

function simplifyGeoJSON(geojson) {
  if (!geojson?.features) return geojson;
  return {
    ...geojson,
    features: geojson.features.map(f => ({
      ...f,
      geometry: {
        ...f.geometry,
        coordinates: simplifyCoords(f.geometry.coordinates),
      },
    })),
  };
}

function getFirstName(name) {
  if (!name) return 'Strada';
  let str = name;

  // Handle stringified arrays like "['A', 'B']" or "[\"A\", \"B\"]"
  if (typeof name === 'string' && name.trim().startsWith('[') && name.trim().endsWith(']')) {
    const match = name.match(/['"](.*?)['"]/);
    if (match) {
      str = match[1];
    }
  }

  if (Array.isArray(str)) {
    str = str[0];
  } else if (typeof str === 'string' && str.includes('/')) {
    str = str.split('/')[0].trim();
  }
  return str;
}

function flowToColor(flow, maxFlow) {
  const t = Math.min(flow / Math.max(maxFlow, 1), 1);
  const YlGnBu = ["#ffffd9", "#edf8b1", "#c7e9b4", "#a1dab4", "#7fcdbb", "#41b6c4", "#1d91c0", "#225ea8", "#253494", "#081d58"];
  return chroma.scale(YlGnBu)(t).hex();
}

function flowToWeight(flow, maxFlow) {
  return 2 + Math.min(flow / Math.max(maxFlow, 1), 1) * 6;
}

function featureIntersectsBounds(feature, bounds) {
  const coords = feature.geometry?.coordinates;
  if (!coords) return false;
  const type = feature.geometry.type;
  let points;
  if (type === 'Point') points = [coords];
  else if (type === 'LineString') points = coords;
  else if (type === 'MultiLineString' || type === 'Polygon') points = coords.flat();
  else if (type === 'MultiPolygon') points = coords.flat(2);
  else return true;

  for (const p of points) {
    if (p[1] >= bounds.south && p[1] <= bounds.north &&
      p[0] >= bounds.west && p[0] <= bounds.east) {
      return true;
    }
  }
  return false;
}

/* ── Components ── */

/** Safely run a Leaflet map operation (avoids _leaflet_pos crashes on torn-down maps). */
function withLiveMap(map, fn) {
  if (!map) return false;
  try {
    const container = map.getContainer?.();
    if (!container?.parentNode || !map._mapPane) return false;
    if (typeof map.stop === 'function') map.stop();
    fn(map);
    return true;
  } catch (e) {
    console.warn('[Assignx Map] Skipped map operation:', e?.message || e);
    return false;
  }
}

function isLiveMap(map) {
  try {
    return !!(map && map.getContainer?.()?.parentNode && map._mapPane);
  } catch {
    return false;
  }
}

function ViewportFilteredGeoJSON({ data, style, onEachFeature, interactive, geoJsonRef }) {
  const map = useMap();
  const [visibleData, setVisibleData] = useState(null);
  const filterTimeoutRef = useRef(null);

  const filterFeatures = useCallback(() => {
    if (!data?.features?.length || !isLiveMap(map)) return;
    try {
      const b = map.getBounds();
      const latBuffer = (b.getNorth() - b.getSouth()) * VIEWPORT_BUFFER;
      const lngBuffer = (b.getEast() - b.getWest()) * VIEWPORT_BUFFER;
      const bounds = {
        south: b.getSouth() - latBuffer,
        north: b.getNorth() + latBuffer,
        west: b.getWest() - lngBuffer,
        east: b.getEast() + lngBuffer,
      };
      const filtered = data.features.filter(f => featureIntersectsBounds(f, bounds));
      setVisibleData({ type: 'FeatureCollection', features: filtered });
    } catch (_) { /* map torn down mid-move */ }
  }, [data, map]);

  useEffect(() => {
    filterFeatures();
    const onMoveEnd = () => {
      if (filterTimeoutRef.current) cancelAnimationFrame(filterTimeoutRef.current);
      filterTimeoutRef.current = requestAnimationFrame(filterFeatures);
    };
    map.on('moveend', onMoveEnd);
    map.on('zoomend', onMoveEnd);
    return () => {
      map.off('moveend', onMoveEnd);
      map.off('zoomend', onMoveEnd);
      if (filterTimeoutRef.current) cancelAnimationFrame(filterTimeoutRef.current);
    };
  }, [map, filterFeatures]);

  if (!visibleData?.features?.length) return null;

  return (
    <GeoJSON
      key={visibleData.features.length + '_' + visibleData.features[0]?.properties?.edge_uid}
      data={visibleData}
      style={style}
      onEachFeature={onEachFeature}
      interactive={interactive}
      ref={geoJsonRef}
    />
  );
}

function MapRefCapture({ mapRef, onMapReady }) {
  const map = useMap();
  useEffect(() => {
    mapRef.current = map;
    if (onMapReady) onMapReady(map);
    return () => {
      try {
        if (typeof map.stop === 'function') map.stop();
      } catch (_) { /* ignore */ }
      if (mapRef.current === map) mapRef.current = null;
    };
  }, [map, mapRef, onMapReady]);
  return null;
}

function ZoomListener({ onZoomChange }) {
  const map = useMap();
  useEffect(() => {
    if (!isLiveMap(map)) return undefined;
    onZoomChange(map.getZoom());
    const handler = () => {
      if (!isLiveMap(map)) return;
      try { onZoomChange(map.getZoom()); } catch (_) { /* ignore */ }
    };
    map.on('zoomend', handler);
    return () => map.off('zoomend', handler);
  }, [map, onZoomChange]);
  return null;
}

function MapClickDismiss({ onMapClick }) {
  const map = useMap();
  useEffect(() => {
    const handler = () => { if (onMapClick) onMapClick(); };
    map.on('click', handler);
    return () => map.off('click', handler);
  }, [map, onMapClick]);
  return null;
}

function FitBounds({ data }) {
  const map = useMap();
  useEffect(() => {
    if (!data?.features?.length || !isLiveMap(map)) return;
    try {
      const sampleSize = Math.min(data.features.length, 200);
      const step = Math.max(1, Math.floor(data.features.length / sampleSize));
      const sampled = { type: 'FeatureCollection', features: [] };
      for (let i = 0; i < data.features.length; i += step) {
        sampled.features.push(data.features[i]);
      }
      const b = L.geoJSON(sampled).getBounds();
      if (b.isValid()) {
        // No animation — avoids _leaflet_pos when compare-mode mounts/unmounts mid-transition
        map.fitBounds(b, { padding: [30, 30], animate: false });
      }
    } catch (_) { }
  }, [data, map]);
  return null;
}

function AssignxSideCard({
  baseFlows,
  closedEdges,
  setClosedEdges,
  highlightEdges,
  onSelectSearchResult,
  streetsByName,
  setHoveredUids
}) {
  const [searchTerm, setSearchTerm] = useState('');

  const searchResults = useMemo(() => {
    if (!searchTerm) return [];
    const term = searchTerm.toLowerCase();
    return Array.from(streetsByName.keys())
      .filter(name => name.toString().toLowerCase().includes(term))
      .sort();
  }, [searchTerm, streetsByName]);

  const selectedSegments = useMemo(() => {
    const groups = new Map();
    if (!baseFlows?.features) return [];

    baseFlows.features.forEach(f => {
      const uid = f.properties.edge_uid;
      if (!closedEdges.has(uid)) return;

      let name = getFirstName(f.properties.name);
      if (!name || name === 'Strada') {
        const parts = uid.split('_');
        name = `Strada (Nodo ${parts[0]})`;
      }

      const groupKey = name.trim();

      if (!groups.has(groupKey)) {
        groups.set(groupKey, {
          name: groupKey,
          uids: [],
          features: []
        });
      }
      const g = groups.get(groupKey);
      g.uids.push(uid);
      g.features.push(f);
    });

    return Array.from(groups.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [closedEdges, baseFlows]);

  const handleSelectStreet = (name) => {
    if (onSelectSearchResult) onSelectSearchResult(name);
  };

  const handleToggleWay = (uid) => {
    setClosedEdges(prev => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      return next;
    });
  };

  const handleRemoveSegment = (allUids) => {
    setClosedEdges(prev => {
      const next = new Set(prev);
      allUids.forEach(uid => next.delete(uid));
      return next;
    });
  };

  return (
    <div className="assignx-side-card-container">
      <div className="assignx-side-card white-theme">
        <div className="assignx-side-header">
          <Input
            placeholder="Cerca strada..."
            prefix={<SearchOutlined />}
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            allowClear
            className="assignx-search-input-v2"
          />
        </div>
        <div className="assignx-side-body">
          {searchTerm ? (
            <List
              size="small"
              dataSource={searchResults}
              renderItem={name => {
                const features = streetsByName.get(name) || [];
                const streetUids = features.map(f => f.properties.edge_uid);
                const isSelected = streetUids.some(uid => closedEdges.has(uid));

                const toggleStreetDir = (isReverse) => {
                  const targets = features.filter(f => {
                    const parts = f.properties.edge_uid.split('_');
                    return isReverse ? (parseInt(parts[0]) > parseInt(parts[1])) : (parseInt(parts[0]) < parseInt(parts[1]));
                  }).map(f => f.properties.edge_uid);

                  setClosedEdges(prev => {
                    const next = new Set(prev);
                    const anyClosed = targets.some(t => next.has(t));
                    if (anyClosed) targets.forEach(t => next.delete(t));
                    else targets.forEach(t => next.add(t));
                    return next;
                  });
                };

                const isDir1Closed = features.some(f => {
                  const parts = f.properties.edge_uid.split('_');
                  return (parseInt(parts[0]) < parseInt(parts[1])) && closedEdges.has(f.properties.edge_uid);
                });
                const isDir2Closed = features.some(f => {
                  const parts = f.properties.edge_uid.split('_');
                  return (parseInt(parts[0]) > parseInt(parts[1])) && closedEdges.has(f.properties.edge_uid);
                });

                return (
                  <div
                    className={`assignx-street-item-v2${isSelected ? ' selected' : ''}`}
                    onClick={() => handleSelectStreet(name)}
                    onMouseEnter={() => {
                      highlightEdges && highlightEdges(streetUids, true);
                      if (onSelectSearchResult) onSelectSearchResult(name);
                      if (setHoveredUids) setHoveredUids(streetUids);
                    }}
                    onMouseLeave={() => {
                      highlightEdges && highlightEdges(streetUids, false);
                      if (setHoveredUids) setHoveredUids(null);
                    }}
                  >
                    <div className="assignx-street-name-v2">{name}</div>
                    <div className="assignx-segment-ways" style={{ marginTop: '4px' }}>
                      <div
                        className={`assignx-way-line-v2 ${isDir1Closed ? 'closed' : 'open'}`}
                        title="Direzione 1"
                        onClick={(e) => { e.stopPropagation(); toggleStreetDir(false); }}
                      />
                      <div
                        className={`assignx-way-line-v2 ${isDir2Closed ? 'closed' : 'open'}`}
                        title="Direzione 2"
                        onClick={(e) => { e.stopPropagation(); toggleStreetDir(true); }}
                      />
                    </div>
                  </div>
                );
              }}
            />
          ) : (
            selectedSegments.length === 0 ? (
              <p className="assignx-empty-text-v2">Nessuna strada selezionata.</p>
            ) : (
              <div className="assignx-segment-list">
                {selectedSegments.map(group => {
                  const name = group.name;
                  const allFeatures = streetsByName.get(name) || group.features;
                  const streetUids = allFeatures.map(f => f.properties.edge_uid);

                  const toggleStreetDir = (isReverse) => {
                    const targets = allFeatures.filter(f => {
                      const parts = f.properties.edge_uid.split('_');
                      return isReverse ? (parseInt(parts[0]) > parseInt(parts[1])) : (parseInt(parts[0]) < parseInt(parts[1]));
                    }).map(f => f.properties.edge_uid);

                    setClosedEdges(prev => {
                      const next = new Set(prev);
                      const anyClosed = targets.some(t => next.has(t));
                      if (anyClosed) targets.forEach(t => next.delete(t));
                      else targets.forEach(t => next.add(t));
                      return next;
                    });
                  };

                  const isDir1Closed = allFeatures.some(f => {
                    const parts = f.properties.edge_uid.split('_');
                    return (parseInt(parts[0]) < parseInt(parts[1])) && closedEdges.has(f.properties.edge_uid);
                  });
                  const isDir2Closed = allFeatures.some(f => {
                    const parts = f.properties.edge_uid.split('_');
                    return (parseInt(parts[0]) > parseInt(parts[1])) && closedEdges.has(f.properties.edge_uid);
                  });

                  return (
                    <div key={group.name} className="assignx-segment-item"
                      onMouseEnter={() => {
                        highlightEdges && highlightEdges(streetUids, true);
                        if (onSelectSearchResult) onSelectSearchResult(name);
                        if (setHoveredUids) setHoveredUids(streetUids);
                      }}
                      onMouseLeave={() => {
                        highlightEdges && highlightEdges(streetUids, false);
                        if (setHoveredUids) setHoveredUids(null);
                      }}
                    >
                      <div className="assignx-segment-header">
                        <span className="assignx-segment-name">{name}</span>
                        <Button
                          type="text" danger size="small"
                          icon={<DeleteOutlined />}
                          className="assignx-segment-delete"
                          onClick={(e) => {
                            e.stopPropagation();
                            setClosedEdges(prev => {
                              const next = new Set(prev);
                              streetUids.forEach(u => next.delete(u));
                              return next;
                            });
                          }}
                        />
                      </div>
                      <div className="assignx-segment-ways">
                        <div
                          className={`assignx-way-line-v2 ${isDir1Closed ? 'closed' : 'open'}`}
                          onClick={(e) => { e.stopPropagation(); toggleStreetDir(false); }}
                        />
                        <div
                          className={`assignx-way-line-v2 ${isDir2Closed ? 'closed' : 'open'}`}
                          onClick={(e) => { e.stopPropagation(); toggleStreetDir(true); }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
}

/* ════════════════ AssignxMapView (Individual Map) ════════════════ */
function AssignxMapView({
  id,
  mapRef,
  mapTheme,
  baseFlows,
  resultFlows,
  closedEdges,
  setClosedEdges,
  selectMode,
  loading,
  running,
  onMapReady,
  edgeUidMap,
  onSelectSearchResult,
  streetsByName,
  externalLayerMapRef,
  hoveredUids,
  baseMax,
  setActiveScenario
}) {
  const [resultMax, setResultMax] = useState(1);
  const [selectionPopup, setSelectionPopup] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(DEFAULT_ZOOM);
  const internalLayerMapRef = useRef(new Map());
  const layerMapRef = externalLayerMapRef || internalLayerMapRef;
  const geoJsonRef = useRef(null);

  const isBase = !resultFlows;
  const closedEdgesRef = useRef(closedEdges);
  const selectModeRef = useRef(selectMode);
  const clickedLayerRef = useRef(false);

  useEffect(() => { closedEdgesRef.current = closedEdges; }, [closedEdges]);
  useEffect(() => { selectModeRef.current = selectMode; }, [selectMode]);

  useEffect(() => {
    if (resultFlows) {
      const mx = resultFlows.features.reduce((a, f) => Math.max(a, f.properties?.flow ?? 0), 1);
      setResultMax(mx);
    }
  }, [resultFlows]);

  // Styles
  const baseStyle = useCallback((feature) => {
    if (closedEdges.has(feature.properties?.edge_uid)) {
      return { color: SEMANTIC_COLORS.danger, weight: 5, opacity: 1, dashArray: '8 4', interactive: true };
    }
    return {
      color: flowToColor(feature.properties?.flow ?? 0, baseMax),
      weight: flowToWeight(feature.properties?.flow ?? 0, baseMax),
      opacity: 0.85, lineJoin: 'round', lineCap: 'round', interactive: true,
    };
  }, [closedEdges, baseMax]);

  const resultStyle = useCallback((feature) => {
    if (closedEdges.has(feature.properties?.edge_uid)) {
      return { color: '#888888', weight: 4, opacity: 0.8, dashArray: '8 4', interactive: true };
    }
    return {
      color: flowToColor(feature.properties?.flow ?? 0, resultMax),
      weight: flowToWeight(feature.properties?.flow ?? 0, resultMax),
      opacity: 0.9, lineJoin: 'round', lineCap: 'round', interactive: true,
    };
  }, [closedEdges, resultMax]);

  const ghostStyle = useCallback((feature) => {
    if (selectMode && closedEdges.has(feature.properties?.edge_uid)) {
      return { color: SEMANTIC_COLORS.danger, weight: 5, opacity: 1, dashArray: '8 4', interactive: true };
    }
    return { color: '#d9d9d9', weight: 2, opacity: 0.5, interactive: true };
  }, [closedEdges, selectMode]);

  useEffect(() => {
    if (!layerMapRef.current) return;
    layerMapRef.current.forEach((layer) => {
      if (layer.feature && typeof layer.setStyle === 'function') {
        const styleFn = isBase ? baseStyle : (layer._isGhost ? ghostStyle : resultStyle);
        layer.setStyle(styleFn(layer.feature));
      }
    });
  }, [closedEdges, isBase, baseStyle, resultStyle, ghostStyle]);

  const parseEdgeUid = (uid) => {
    if (!uid) return null;
    const parts = uid.split('_');
    if (parts.length < 3) return null;
    return { u: parts[0], v: parts[1], k: parts.slice(2).join('_') };
  };
  const getReverseEdgeUids = useCallback((edgeUid) => {
    if (!edgeUidMap) return [];
    const parsed = parseEdgeUid(edgeUid);
    if (!parsed) return [];
    const reverseKey = `${parsed.v}_${parsed.u}`;
    return edgeUidMap.get(reverseKey) ?? [];
  }, [edgeUidMap]);

  const getSplitFeature = useCallback((feature, direction) => {
    try {
      const scale = Math.pow(2, 17 - zoomLevel);
      const offsetVal = 0.00003 * Math.min(Math.max(scale, 0.1), 50);
      const offset = direction === 'forward' ? offsetVal : -offsetVal;
      const line = turf.helpers.feature(feature.geometry);
      const offsetLine = turf.lineOffset(line, offset, { units: 'degrees' });
      return {
        ...feature,
        geometry: offsetLine.geometry,
        id: `${feature.properties.edge_uid}_${direction}`
      };
    } catch (e) {
      return feature;
    }
  }, [zoomLevel]);

  const openClosurePopup = useCallback((edgeUid, layer, latlng) => {
    if (!selectModeRef.current || !edgeUid) return;
    const reverseUids = getReverseEdgeUids(edgeUid);
    const currentClosed = closedEdgesRef.current;
    const streetName = getFirstName(layer.feature?.properties?.name);
    const checkedState = {
      [edgeUid]: currentClosed.has(edgeUid),
      ...Object.fromEntries(reverseUids.map(uid => [uid, currentClosed.has(uid)]))
    };
    setSelectionPopup({ edgeUid, reverseUids, streetName, checked: checkedState, latlng });
  }, [getReverseEdgeUids]);

  const onFeature = useCallback((feature, layer, isGhost = false) => {
    const { edge_uid } = feature.properties ?? {};
    layerMapRef.current.set(edge_uid, layer);
    if (isGhost) layer._isGhost = true;
    if (layer.setStyle) layer.setStyle({ interactive: true });

    const updateTooltip = () => {
      const name = getFirstName(feature.properties?.name);
      const flowVal = (feature.properties?.flow ?? 0).toFixed(2);
      const timeVal = (feature.properties?.travel_time ?? 0).toFixed(2);
      const tip = `<div class="assignx-tooltip">
          <strong>${name}</strong><br/>
          <span>Flusso: ${flowVal}</span><br/>
          <span>Tempo: ${timeVal}s</span>
          ${selectModeRef.current ? `<br/><em class="tip-action">+ Clicca per scegliere la direzione di chiusura</em>` : ''}
         </div>`;
      if (layer.getTooltip()) layer.setTooltipContent(tip);
      else layer.bindTooltip(tip, { sticky: true, direction: 'top', className: 'assignx-tooltip-container', interactive: true });
    };
    updateTooltip();
    layer.on('mouseover', updateTooltip);

    layer.on('click', (e) => {
      if (!selectModeRef.current) return;
      clickedLayerRef.current = true;
      setTimeout(() => { clickedLayerRef.current = false; }, 50);
      withLiveMap(mapRef.current, (map) => {
        map.flyTo(e.latlng, 18, { duration: 0.5 });
      });
      openClosurePopup(edge_uid, layer, e.latlng);
    });
  }, [openClosurePopup, mapRef]);

  const highlightEdges = useCallback((edgeUids, active) => {
    if (!edgeUids) return;
    edgeUids.forEach(uid => {
      const layer = layerMapRef.current.get(uid);
      if (layer) {
        if (active) {
          layer.setStyle({ color: '#8C74FF', weight: 8, opacity: 1 });
          if (layer.bringToFront) layer.bringToFront();
        } else {
          const isGhost = layer._isGhost;
          const styleFn = isBase ? baseStyle : (isGhost ? ghostStyle : resultStyle);
          if (layer.feature) layer.setStyle(styleFn(layer.feature));
        }
      }
    });
  }, [baseStyle, resultStyle, ghostStyle, isBase]);

  const updateSelectionPopupChecked = (uid, checked) => {
    setSelectionPopup(prev => prev ? { ...prev, checked: { ...prev.checked, [uid]: checked } } : prev);
  };
  const applySelectionPopup = () => {
    if (!selectionPopup) return;
    const { checked, edgeUid, reverseUids } = selectionPopup;
    setClosedEdges(prev => {
      const next = new Set(prev);
      [edgeUid, ...reverseUids].forEach(uid => {
        if (checked[uid]) next.add(uid);
        else next.delete(uid);
      });
      return next;
    });
    setSelectionPopup(null);
  };

  const tileUrl = mapTheme === 'satellite'
    ? 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
    : (mapTheme === 'dark'
      ? `https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png?key=${process.env.REACT_APP_CARTO_KEY}`
      : `https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png?key=${process.env.REACT_APP_CARTO_KEY}`);

  return (
    <div className="assignx-map-view">
      <MapContainer
        center={DEFAULT_CENTER} zoom={DEFAULT_ZOOM}
        maxZoom={22} zoomControl={false} className="assignx-map"
        preferCanvas={true}
      >
        <Pane name="split-roads-pane" style={{ zIndex: 1000, pointerEvents: 'none' }} />
        <MapRefCapture mapRef={mapRef} onMapReady={onMapReady} />
        <ZoomListener onZoomChange={setZoomLevel} />
        <MapClickDismiss onMapClick={() => {
          if (!clickedLayerRef.current) setSelectionPopup(null);
        }} />
        <TileLayer url={tileUrl} maxZoom={26} opacity={mapTheme === 'satellite' ? 1.0 : 0.8} />

        {isBase && baseFlows && (
          <>
            <ViewportFilteredGeoJSON
              data={baseFlows} style={baseStyle}
              onEachFeature={(f, l) => onFeature(f, l, false)}
              interactive={true} geoJsonRef={geoJsonRef}
            />
            {selectionPopup && baseFlows.features.find(f => f.properties.edge_uid === selectionPopup.edgeUid) && (
              <>
                {(() => {
                  const feat = baseFlows.features.find(f => f.properties.edge_uid === selectionPopup.edgeUid);
                  const forward = getSplitFeature(feat, 'forward');
                  const backward = getSplitFeature(feat, 'backward');
                  return (
                    <>
                      <GeoJSON
                        key={`split-fw-${feat.properties.edge_uid}`} data={forward}
                        style={{ color: selectionPopup.checked[feat.properties.edge_uid] ? '#ff4d4f' : '#52c41a', weight: 8, opacity: 1 }}
                        pane="split-roads-pane" interactive={false}
                      />
                      <GeoJSON
                        key={`split-bw-${feat.properties.edge_uid}`} data={backward}
                        style={{ color: selectionPopup.checked[selectionPopup.reverseUids[0]] ? '#ff4d4f' : '#52c41a', weight: 8, opacity: 1 }}
                        pane="split-roads-pane" interactive={false}
                      />
                    </>
                  );
                })()}
              </>
            )}
            <FitBounds data={baseFlows} />
          </>
        )}

        {hoveredUids && baseFlows && (() => {
          const processed = new Set();
          const pairs = [];
          for (const uid of hoveredUids) {
            if (processed.has(uid)) continue;
            processed.add(uid);
            const parts = uid.split('_');
            const reversePattern = `${parts[1]}_${parts[0]}`;
            const reverseFeat = baseFlows.features.find(f =>
              f.properties.edge_uid.startsWith(reversePattern) && hoveredUids.includes(f.properties.edge_uid)
            );
            const reverseUid = reverseFeat?.properties?.edge_uid;
            if (reverseUid) processed.add(reverseUid);
            const forwardFeat = baseFlows.features.find(f => f.properties.edge_uid === uid);
            if (forwardFeat) pairs.push({ forwardFeat, forwardUid: uid, reverseUid });
          }
          return pairs.map(({ forwardFeat, forwardUid, reverseUid }) => {
            const forward = getSplitFeature(forwardFeat, 'forward');
            const backward = getSplitFeature(forwardFeat, 'backward');
            return (
              <React.Fragment key={`hover-${forwardUid}`}>
                <GeoJSON data={forward} style={{ color: closedEdges.has(forwardUid) ? '#ff4d4f' : '#52c41a', weight: 8, opacity: 1 }} pane="split-roads-pane" interactive={false} />
                {reverseUid && <GeoJSON data={backward} style={{ color: closedEdges.has(reverseUid) ? '#ff4d4f' : '#52c41a', weight: 8, opacity: 1 }} pane="split-roads-pane" interactive={false} />}
              </React.Fragment>
            );
          });
        })()}

        {!isBase && baseFlows && (
          <GeoJSON key="ghost" data={baseFlows} style={ghostStyle} onEachFeature={(f, l) => onFeature(f, l, true)} interactive={true} />
        )}

        {!isBase && resultFlows && (
          <>
            <GeoJSON key={`result-${resultFlows.features.length}`} data={resultFlows} style={resultStyle} onEachFeature={(f, l) => onFeature(f, l, false)} />
            <FitBounds data={resultFlows} />
          </>
        )}

        <div className="assignx-map-controls">
          <CurrentLocationButton />
          <ZoomButtons />
        </div>

        <div className="assignx-legend">
          <div className="assignx-legend-title">Flusso Traffico</div>
          <div className="legend-gradient-container">
            <div className="legend-gradient-bar gradient-assignx-flow"></div>
            <div className="legend-labels">
              <span className="legend-label">Basso</span>
              <span className="legend-label">Alto</span>
            </div>
          </div>
        </div>
      </MapContainer>

      {selectionPopup && isLiveMap(mapRef.current) && (() => {
        let point;
        try {
          point = mapRef.current.latLngToContainerPoint(selectionPopup.latlng);
        } catch (_) {
          return null;
        }
        return (
          <div className="assignx-selection-popup-v2" style={{ position: 'absolute', top: point.y, left: point.x, transform: 'translate(-50%, -105%)', zIndex: 100000 }} onMouseDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
            <button className="assignx-popup-v2-close" onClick={() => setSelectionPopup(null)}><CloseOutlined /></button>
            <div className="assignx-popup-v2-header">
              <div className="assignx-popup-v2-title">{selectionPopup.streetName}</div>
              <div className="assignx-popup-v2-subtitle"><span className="telemetry-dot" /> ASSETTO STRADALE</div>
            </div>
            <div className="assignx-popup-v2-body">
              {[selectionPopup.edgeUid, ...selectionPopup.reverseUids].map((uid) => {
                const isClosed = selectionPopup.checked[uid];
                const isReverse = uid !== selectionPopup.edgeUid;
                return (
                  <div key={uid} className={`assignx-popup-card ${isClosed ? 'closed' : 'operative'}`}>
                    <div className="assignx-popup-card-left">
                      <div className="assignx-popup-icon-wrapper" style={{ background: isClosed ? '#ff4d4f' : '#52c41a' }}>
                        {isClosed ? <StopOutlined className="assignx-status-icon" style={{ color: 'white' }} /> : (isReverse ? <ArrowLeftOutlined className="assignx-status-icon" style={{ color: 'white' }} /> : <ArrowRightOutlined className="assignx-status-icon" style={{ color: 'white' }} />)}
                      </div>
                      <div className="assignx-popup-card-info">
                        <div className="assignx-popup-dir-name" style={{ color: isClosed ? '#ff4d4f' : '#52c41a' }}>{isReverse ? 'DIREZIONE OPPOSTA' : 'DIREZIONE PRINCIPALE'}</div>
                        <div className="assignx-popup-status-text">{isClosed ? 'CHIUSO' : 'APERTO'}</div>
                      </div>
                    </div>
                    <Switch checked={!isClosed} onChange={(val) => updateSelectionPopupChecked(uid, !val)} className="assignx-popup-switch" style={{ backgroundColor: !isClosed ? '#52c41a' : '#ff4d4f' }} />
                  </div>
                )
              })}
            </div>
            <div className="assignx-popup-v2-footer">
              <Button type="primary" block className="assignx-confirm-assetto-btn" style={{ background: '#353A0A' }} onClick={applySelectionPopup}>CONFERMA ASSETTO</Button>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

/* ════════════════ AssignxPage (Controller) ════════════════ */
export default function AssignxPage() {
  const { showLoader, hideLoader } = useLoading();
  const [mapTheme, setMapTheme] = useState('dark');
  const [baseFlows, setBaseFlows] = useState(null);
  const [loadingNet, setLoadingNet] = useState(true);
  const [selectMode, setSelectMode] = useState(false);
  const [compareMode, setCompareMode] = useState(false);
  const [messageApi, ctx] = message.useMessage();

  const baseMax = useMemo(() => {
    if (!baseFlows?.features) return 1;
    return baseFlows.features.reduce((a, f) => Math.max(a, f.properties?.flow ?? 0), 1);
  }, [baseFlows]);

  const [resultFlowsA, setResultFlowsA] = useState(null);
  const [closedEdgesA, setClosedEdgesA] = useState(new Set());
  const [runningA, setRunningA] = useState(false);
  const mapARef = useRef(null);
  const layerMapARef = useRef(new Map());

  const [resultFlowsB, setResultFlowsB] = useState(null);
  const [closedEdgesB, setClosedEdgesB] = useState(new Set());
  const [runningB, setRunningB] = useState(false);
  const mapBRef = useRef(null);
  const layerMapBRef = useRef(new Map());

  const [lastSimulatedEdges, setLastSimulatedEdges] = useState(new Set());
  const [lastSimulatedFlows, setLastSimulatedFlows] = useState(null);

  useEffect(() => {
    if (loadingNet || runningA || runningB) {
      const isSimulating = runningA || runningB;
      showLoader('Elaborazione Simulazione Assignx...', 1, { showPercentage: !isSimulating });
    } else {
      hideLoader();
    }
  }, [loadingNet, runningA, runningB]);

  const [hoveredUids, setHoveredUids] = useState(null);

  const edgeUidMap = useMemo(() => {
    const map = new Map();
    if (!baseFlows?.features) return map;
    for (const f of baseFlows.features) {
      const uid = f?.properties?.edge_uid;
      if (!uid) continue;
      const parts = uid.split('_');
      if (parts.length < 3) continue;
      const key = `${parts[0]}_${parts[1]}`;
      const arr = map.get(key) ?? [];
      arr.push(uid);
      map.set(key, arr);
    }
    return map;
  }, [baseFlows]);

  useEffect(() => {
    const loadBase = async () => {
      try {
        const clientType = getClientType();
        let data;
        try {
          data = await fetchClientJSON(clientType, BASE_FLOWS_FILE);
        } catch (e) {
          const res = await fetch(`/${BASE_FLOWS_FILE}`);
          if (!res.ok) throw new Error("Load failed");
          data = await res.json();
        }
        setBaseFlows(simplifyGeoJSON(data));
      } catch (err) {
        messageApi.error("Impossibile caricare la rete base");
      } finally {
        setLoadingNet(false);
      }
    };
    loadBase();
  }, []);

  const isSyncing = useRef(false);
  const syncMaps = useCallback((sourceMap, targetMap) => {
    if (!sourceMap || !targetMap || isSyncing.current) return;
    const onMove = () => {
      if (isSyncing.current) return;
      if (!isLiveMap(sourceMap) || !isLiveMap(targetMap)) return;
      isSyncing.current = true;
      try {
        const center = sourceMap.getCenter();
        const zoom = sourceMap.getZoom();
        targetMap.setView(center, zoom, { animate: false });
      } catch (_) { /* target may be mid-unmount */ }
      isSyncing.current = false;
    };
    sourceMap.on('move', onMove);
    return () => {
      try { sourceMap.off('move', onMove); } catch (_) { /* ignore */ }
    };
  }, []);

  useEffect(() => {
    if (compareMode && mapARef.current && mapBRef.current) {
      const cleanA = syncMaps(mapARef.current, mapBRef.current);
      const cleanB = syncMaps(mapBRef.current, mapARef.current);
      return () => {
        if (cleanA) cleanA();
        if (cleanB) cleanB();
        withLiveMap(mapARef.current, (m) => { /* stop only */ });
        withLiveMap(mapBRef.current, (m) => { /* stop only */ });
      };
    }
    return undefined;
  }, [compareMode, syncMaps]);

  const handleToggleCompare = () => {
    if (!compareMode) {
      if (!lastSimulatedFlows) {
        messageApi.warning("Esegui una simulazione prima di attivare il confronto");
        return;
      }
      // Restore the previous scenario into Map B for editing in comparison mode
      setClosedEdgesB(new Set(lastSimulatedEdges));
      setResultFlowsB(lastSimulatedFlows);
      setSelectMode(false);
    }
    setCompareMode(!compareMode);
  };


  const runAssignment = async (edges, setRes, setRun) => {
    setRun(true);
    const clientType = getClientType();
    try {
      const res = await fetch(`${API_BASE}/api/run_assignment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ closed_edge_uids: [...edges], client_type: clientType })
      });
      if (!res.ok) throw new Error("Errore API");
      const data = await res.json();
      setRes(data);
      if (!compareMode) {
        setLastSimulatedEdges(new Set(edges));
        setLastSimulatedFlows(data);
      }
      messageApi.success("Assegnazione aggiornata");
    } catch (e) {
      messageApi.error("Assegnazione fallita: " + e.message);
    } finally {
      setRun(false);
    }
  };

  const streetsByName = useMemo(() => {
    const map = new Map();
    if (!baseFlows?.features) return map;
    for (const f of baseFlows.features) {
      let name = getFirstName(f.properties?.name);
      if (!name || name === 'Strada') {
        const edgeUid = f.properties?.edge_uid;
        if (edgeUid) {
          const parts = edgeUid.split('_');
          name = parts ? `Strada (Nodo ${parts[0]})` : 'Strada sconosciuta';
        }
      }
      const uid = f.properties?.edge_uid;
      if (uid && name) {
        const key = name.trim();
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(f);
      }
    }
    return map;
  }, [baseFlows]);

  const handleRunAll = () => {
    setSelectMode(false);
    if (compareMode) runAssignment(closedEdgesB, setResultFlowsB, setRunningB);
    else runAssignment(closedEdgesA, setResultFlowsA, setRunningA);
  };

  const handleSelectSearchResult = (name) => {
    const features = streetsByName.get(name);
    if (!features || !features.length) return;
    const targetMap = compareMode ? mapBRef.current : mapARef.current;
    if (!targetMap) return;
    const bounds = L.geoJSON(features).getBounds();
    if (bounds.isValid()) {
      withLiveMap(targetMap, (map) => {
        map.fitBounds(bounds, { padding: [100, 100], maxZoom: 19, animate: false });
      });
    }
  };

  const highlightEdgesInMap = useCallback((uids, active, layerMapRef, resultFlows, closedEdges) => {
    if (!uids || !layerMapRef.current) return;
    uids.forEach(uid => {
      const layer = layerMapRef.current.get(uid);
      if (layer) {
        if (active) {
          layer.setStyle({ color: '#8C74FF', weight: 8, opacity: 1 });
          if (layer.bringToFront) layer.bringToFront();
        } else {
          if (layer.feature) {
            const isClosed = closedEdges.has(uid);
            const isBase = !resultFlows;
            if (isClosed) {
              layer.setStyle({ color: SEMANTIC_COLORS.danger, weight: 5, opacity: 1, dashArray: '8 4' });
            } else {
              const max = isBase ? baseMax : resultFlows.features.reduce((a, f) => Math.max(a, f.properties?.flow ?? 0), 1);
              layer.setStyle({
                color: flowToColor(layer.feature.properties?.flow ?? 0, max),
                weight: flowToWeight(layer.feature.properties?.flow ?? 0, max),
                opacity: 0.85
              });
            }
          }
        }
      }
    });
  }, [baseMax]);

  const highlightEdgesA = useCallback((uids, active) => {
    highlightEdgesInMap(uids, active, layerMapARef, compareMode ? lastSimulatedFlows : resultFlowsA, compareMode ? lastSimulatedEdges : closedEdgesA);
  }, [highlightEdgesInMap, compareMode, lastSimulatedFlows, resultFlowsA, lastSimulatedEdges, closedEdgesA]);

  const highlightEdgesB = useCallback((uids, active) => {
    highlightEdgesInMap(uids, active, layerMapBRef, resultFlowsB, closedEdgesB);
  }, [highlightEdgesInMap, resultFlowsB, closedEdgesB]);

  const onMapReady = (map) => { /* map setup if needed */ };

  return (
    <div className={`assignx-page ${compareMode ? 'compare-mode' : ''} ${selectMode ? 'select-mode-active' : ''}`}>
      {ctx}
      <div className="assignx-top-controls" style={{ right: '20px', left: 'auto' }}>
        <AntTooltip title={selectMode ? "Chiudi Selezione" : "Seleziona Tratti"} placement="bottom">
          <button className={`sidebar-button ${selectMode ? 'active' : ''}`} onClick={() => setSelectMode(!selectMode)}><SelectOutlined /></button>
        </AntTooltip>
        <AntTooltip title="Avvia Simulazione" placement="bottom">
          <button className={`sidebar-button ${runningA || runningB ? 'active' : ''}`} onClick={handleRunAll} disabled={runningA || runningB}><PlayCircleOutlined /></button>
        </AntTooltip>
        <AntTooltip title={compareMode ? "Esci dal Confronto" : "Compara Scenario Base con l'ultima Simulazione"} placement="bottom">
          <button className={`sidebar-button-confronto ${compareMode ? 'active' : ''}`} onClick={handleToggleCompare} disabled={selectMode}>
            {compareMode ? <FullscreenOutlined /> : <img src={ExchangeIcon} alt="Confronto" className="confronto-icon" />}
            <span>{compareMode ? 'Esci' : 'Confronto'}</span>
          </button>
        </AntTooltip>
      </div>

      {selectMode && (
        <div className="assignx-select-banner">MODALITÀ SELEZIONE ATTIVA - Seleziona i rami stradali per configurarli</div>
      )}

      <div className="assignx-split-container">
        <AssignxMapView
          id="mapA" mapRef={mapARef} mapTheme={mapTheme} baseFlows={baseFlows}
          resultFlows={compareMode ? null : resultFlowsA}
          closedEdges={compareMode ? new Set() : closedEdgesA}
          setClosedEdges={compareMode ? () => { } : setClosedEdgesA}
          selectMode={compareMode ? false : selectMode}
          loading={loadingNet} running={runningA} edgeUidMap={edgeUidMap}
          onSelectSearchResult={handleSelectSearchResult} onMapReady={onMapReady}
          streetsByName={streetsByName} externalLayerMapRef={layerMapARef}
          hoveredUids={hoveredUids} baseMax={baseMax}
        />
        {compareMode && (
          <AssignxMapView
            id="mapB" mapRef={mapBRef} mapTheme={mapTheme} baseFlows={baseFlows}
            resultFlows={resultFlowsB} closedEdges={closedEdgesB}
            setClosedEdges={setClosedEdgesB} selectMode={selectMode}
            loading={loadingNet} running={runningB} edgeUidMap={edgeUidMap}
            onSelectSearchResult={handleSelectSearchResult}
            streetsByName={streetsByName} externalLayerMapRef={layerMapBRef}
            hoveredUids={hoveredUids} baseMax={baseMax}
          />
        )}
      </div>

      <AssignxSideCard
        baseFlows={baseFlows}
        closedEdges={compareMode ? closedEdgesB : closedEdgesA}
        setClosedEdges={compareMode ? setClosedEdgesB : setClosedEdgesA}
        highlightEdges={compareMode ? highlightEdgesB : highlightEdgesA}
        onSelectSearchResult={handleSelectSearchResult}
        streetsByName={streetsByName}
        setHoveredUids={setHoveredUids}
      />

      <div className="assignx-theme-toggle">
        <AntTooltip title={mapTheme === 'satellite' ? 'Passa a tema chiaro' : (mapTheme === 'dark' ? 'Passa a satellite' : 'Passa a tema scuro')} placement="left">
          <button className="theme-toggle-button" onClick={() => setMapTheme(p => p === 'light' ? 'dark' : (p === 'dark' ? 'satellite' : 'light'))}>
            {mapTheme === 'satellite' ? <MdSatelliteAlt /> : (mapTheme === 'dark' ? <MdLightMode /> : <MdDarkMode />)}
          </button>
        </AntTooltip>
      </div>

      <ModuleSlider
        showNavbarControls={false} setShowNavbarControls={() => { }}
        showSimfixAreas={false} setShowSimfixAreas={() => { }}
        showPredictMode={false} setShowPredictMode={() => { }}
        showTlGreenWave={false} setShowTlGreenWave={() => { }}
        showAssignx={true} setShowAssignx={() => { }}
      />
    </div>
  );
}
